import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useGoBack, useOnChange, useUrlFilters, useUrlState } from '../../hooks/useUrlState';
import Swal from 'sweetalert2';
import api from '../../utils/api';
import RecentOptions from '../../components/RecentOptions';
import { recordRecentPick } from '../../utils/recentPicks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSearch, faTimes, faArrowLeft, faCircleCheck, faTriangleExclamation,
    faClock, faHourglassHalf, faUsersRectangle, faRepeat, faLock
} from '@fortawesome/free-solid-svg-icons';
import DateRangeFilter from '../../components/DateRangeFilter';
import Avatar from '../../components/Avatar';
import Pagination from '../../components/Pagination';
import { useRecentSearches } from '../../components/RecentSearches';
import TaskDetailDrawer from '../../components/TaskDetailDrawer';
import { orgLabel } from '../../utils/orgTaxonomy';
import { shortDate } from '../../utils/taskHelpers';
import '../../styles/App.css';
import '../../styles/tasks.css';
import '../../styles/performance.css';

/**
 * One employee's record behind their score — feature draft F6.1/F6.9.
 *
 * The point of this page is the comparison the board can only summarise: for
 * every task, what the deadline was and when it actually landed. Everything
 * else is here to let you narrow to the cases worth looking at.
 *
 * The header score deliberately does NOT move when the list filters change.
 * Filtering to "On Hold" and watching the score drop to 0% would read as a
 * verdict on the person rather than on the filter; period and project are the
 * only two controls that change what is being scored.
 */

const DEFAULT_PERIOD = 'Month';

const OUTCOME = {
    onTime: { label: 'On time', cls: 'completed', icon: faCircleCheck },
    late: { label: 'Late', cls: 'onhold', icon: faClock },
    missed: { label: 'Missed', cls: 'blocked', icon: faTriangleExclamation },
    open: { label: 'In hand', cls: 'today', icon: faHourglassHalf }
};

const OUTCOME_OPTIONS = [
    { value: 'All', label: 'Every outcome' },
    { value: 'onTime', label: 'On time' },
    { value: 'late', label: 'Late' },
    { value: 'missed', label: 'Missed' },
    { value: 'open', label: 'Still in hand' }
];

const STATUS_OPTIONS = ['All', 'Pending', 'In Progress', 'On Hold', 'Completed'];
const PRIORITY_OPTIONS = ['All', 'Urgent', 'High', 'Medium', 'Low'];
const TYPE_OPTIONS = [
    { value: 'All', label: 'All task types' },
    { value: 'Project Task', label: 'Project work' },
    { value: 'Regular Office Task', label: 'Office work' }
];

const PERIOD_LABEL = {
    Today: 'due today',
    Yesterday: 'due yesterday',
    Week: 'due in the last 7 days',
    Month: 'due in the last 30 days',
    All: 'all time',
    Custom: 'in the selected range'
};

// A task generated by a recurring schedule belongs to its occurrenceDate; the
// dueDate it carries is the schedule's end-of-day and can read as the next day.
const dayOf = (task) => task.occurrenceDate || task.dueDate;

// Managers and Team Leads reach this page from a chat profile and can't open
// the company board, so Back returns them to wherever they came from.
const canSeeBoard = () => {
    try {
        return ['ADMIN', 'HR'].includes(JSON.parse(localStorage.getItem('user'))?.role);
    } catch (e) {
        return false;
    }
};

const EmployeePerformance = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [fromBoard] = useState(canSeeBoard);
    const goBack = useGoBack(fromBoard ? '/performance' : '/dashboard');

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Scoring controls — these change the header number.
    const [period, setPeriod] = useUrlState('period', DEFAULT_PERIOD);
    const [customRange, setCustomRange] = useUrlFilters({ from: '', to: '' });
    const [projectId, setProjectId] = useUrlState('project', 'All');
    const [projects, setProjects] = useState([]);

    // List controls — these narrow the table only.
    const [outcome, setOutcome] = useUrlState('outcome', 'All');
    const [status, setStatus] = useUrlState('status', 'All');
    const [priority, setPriority] = useUrlState('priority', 'All');
    const [taskType, setTaskType] = useUrlState('type', 'All');
    // The address holds the search once typing pauses; the box starts from it.
    const [debouncedSearch, setDebouncedSearch] = useUrlState('q', '');
    const [search, setSearch] = useState(debouncedSearch);
    const recentSearch = useRecentSearches('employee-performance', { value: search, onPick: setSearch });

    const [page, setPage] = useUrlState('page', 1);
    const [limit, setLimit] = useState(20);

    // The row the drawer is showing. Holding the outcome alongside the id
    // means the drawer states the same verdict the row does, rather than
    // working it out a second time from the task it fetches.
    const [openTask, setOpenTask] = useState(null);

    useEffect(() => {
        api.get('/tasks/assignable-projects')
            .then(res => setProjects(res.data || []))
            .catch(() => setProjects([]));
    }, []);

    // Typing shouldn't fire a request per keystroke — this list is server-side.
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search), 350);
        return () => clearTimeout(t);
    }, [search, setDebouncedSearch]);

    // Any filter change invalidates the current page number.
    useOnChange(() => setPage(1), [period, customRange.from, customRange.to, projectId, outcome, status, priority, taskType, debouncedSearch]);

    const rangeReady = period !== 'Custom' || customRange.from || customRange.to;

    const load = useCallback(() => {
        if (!rangeReady) return;
        setLoading(true);

        api.get(`/analytics/performance/${id}`, {
            params: {
                filterType: period,
                fromDate: period === 'Custom' ? customRange.from : undefined,
                toDate: period === 'Custom' ? customRange.to : undefined,
                projectId: projectId !== 'All' ? projectId : undefined,
                outcome: outcome !== 'All' ? outcome : undefined,
                status: status !== 'All' ? status : undefined,
                priority: priority !== 'All' ? priority : undefined,
                taskType: taskType !== 'All' ? taskType : undefined,
                search: debouncedSearch || undefined,
                page,
                limit
            }
        })
            .then(res => setData(res.data))
            .catch(err => {
                console.error('Failed to load employee performance', err);
                Swal.fire('Error', err.response?.data?.message || 'Could not load this employee.', 'error');
                goBack();
            })
            .finally(() => setLoading(false));
    }, [id, period, customRange.from, customRange.to, projectId, outcome, status,
        priority, taskType, debouncedSearch, page, limit, rangeReady, goBack]);

    useEffect(() => { load(); }, [load]);

    const activeFilterCount =
        (outcome !== 'All' ? 1 : 0) + (status !== 'All' ? 1 : 0) +
        (priority !== 'All' ? 1 : 0) + (taskType !== 'All' ? 1 : 0) + (search ? 1 : 0);

    const clearFilters = () => {
        setOutcome('All'); setStatus('All'); setPriority('All'); setTaskType('All'); setSearch('');
    };

    if (!data && loading) {
        return (
            <div className="attendance-container fade-in">
                <div className="control-card text-center" style={{ padding: '40px' }}>
                    <p className="text-muted">Loading...</p>
                </div>
            </div>
        );
    }
    if (!data) return null;

    const { employee, counts, score, settled, accent } = data;
    const periodLabel = PERIOD_LABEL[period] || 'in the selected range';

    return (
        <div className="attendance-container fade-in">
            <button className="gts-btn secondary perf-back" onClick={goBack}>
                <FontAwesomeIcon icon={faArrowLeft} /> {fromBoard ? 'Back to Performance' : 'Back'}
            </button>

            <div className={`control-card perf-header ${accent}`}>
                <Avatar name={employee.name} profilePic={employee.profilePic} className="perf-header-avatar" />
                <div className="perf-header-identity">
                    <h1 className="perf-header-name">{employee.name}</h1>
                    <span className="perf-header-meta">
                        {[employee.employeeId, orgLabel(employee.jobTitle) || employee.role, orgLabel(employee.department)]
                            .filter(Boolean).join(' · ')}
                    </span>
                </div>
                <div className="perf-header-score">
                    {score === null ? (
                        <>
                            <span className="perf-score is-empty">—</span>
                            <span className="perf-score-sub">No deadlines {periodLabel}</span>
                        </>
                    ) : (
                        <>
                            <span className="perf-score">{score}<small>%</small></span>
                            <span className="perf-score-sub">
                                on time · {settled} of {counts.onTime + counts.late + counts.missed} settled
                            </span>
                        </>
                    )}
                </div>
            </div>

            <DateRangeFilter
                value={period}
                onChange={setPeriod}
                custom={customRange}
                onCustomChange={setCustomRange}
            />

            <div className="emp-stat-row perf-outcome-row">
                {['onTime', 'late', 'missed', 'open'].map(key => (
                    <button
                        key={key}
                        type="button"
                        className={`emp-stat ${outcome === key ? 'active' : ''}`}
                        onClick={() => setOutcome(outcome === key ? 'All' : key)}
                    >
                        <span className={`emp-stat-icon ${OUTCOME[key].cls === 'completed' ? 'done'
                            : OUTCOME[key].cls === 'blocked' ? 'late'
                                : OUTCOME[key].cls === 'onhold' ? 'idle' : 'today'}`}>
                            <FontAwesomeIcon icon={OUTCOME[key].icon} />
                        </span>
                        <span className="emp-stat-text">
                            <strong>{counts[key]}</strong>
                            <small>{OUTCOME[key].label}</small>
                        </span>
                    </button>
                ))}
            </div>

            <div className="task-toolbar">
                <div className="task-toolbar-search">
                    <FontAwesomeIcon icon={faSearch} className="task-search-icon" />
                    <input
                        {...recentSearch.inputProps}
                        type="text"
                        placeholder="Search these tasks by title or description..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    {recentSearch.menu}
                </div>
                <div className="task-toolbar-filters">
                    <select
                        className={`task-filter-select ${projectId !== 'All' ? 'is-active' : ''}`}
                        value={projectId}
                        onChange={(e) => { setProjectId(e.target.value); recordRecentPick('project', e.target.value); }}
                    >
                        <option value="All">All Projects</option>
                        <RecentOptions kind="project" items={projects} />
                    </select>
                    <select
                        className={`task-filter-select ${outcome !== 'All' ? 'is-active' : ''}`}
                        value={outcome}
                        onChange={(e) => setOutcome(e.target.value)}
                    >
                        {OUTCOME_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <select
                        className={`task-filter-select ${status !== 'All' ? 'is-active' : ''}`}
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                    >
                        {STATUS_OPTIONS.map(s => (
                            <option key={s} value={s}>{s === 'All' ? 'Any status' : s}</option>
                        ))}
                    </select>
                    <select
                        className={`task-filter-select ${priority !== 'All' ? 'is-active' : ''}`}
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                    >
                        {PRIORITY_OPTIONS.map(p => (
                            <option key={p} value={p}>{p === 'All' ? 'Any priority' : p}</option>
                        ))}
                    </select>
                    <select
                        className={`task-filter-select ${taskType !== 'All' ? 'is-active' : ''}`}
                        value={taskType}
                        onChange={(e) => setTaskType(e.target.value)}
                    >
                        {TYPE_OPTIONS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    {activeFilterCount > 0 && (
                        <button className="task-filter-clear" onClick={clearFilters}>
                            <FontAwesomeIcon icon={faTimes} /> Clear ({activeFilterCount})
                        </button>
                    )}
                </div>
            </div>

            <div className="task-result-count">
                Showing {data.data.length} of {data.pagination.totalRecords} task
                {data.pagination.totalRecords === 1 ? '' : 's'} {periodLabel}
                {activeFilterCount > 0 && ' (filtered)'}
            </div>

            {data.data.length === 0 ? (
                <div className="control-card text-center" style={{ padding: '40px' }}>
                    <p className="text-muted">
                        {activeFilterCount > 0 ? 'No tasks match these filters' : 'No tasks in this period'}
                    </p>
                    {activeFilterCount > 0 && (
                        <button className="gts-btn secondary" onClick={clearFilters}>Reset filters</button>
                    )}
                </div>
            ) : (
                <div className="perf-table">
                    <div className="perf-row perf-row-head">
                        <span>Task</span>
                        <span>Due</span>
                        <span>Finished</span>
                        <span>Outcome</span>
                    </div>
                    {data.data.map(task => (
                        <div
                            className="perf-row"
                            key={task._id}
                            role="button"
                            tabIndex={0}
                            aria-haspopup="dialog"
                            // A masked private row counts towards the score but
                            // has no detail this viewer may open.
                            onClick={() => { if (!task.isMasked) { recentSearch.save(); setOpenTask({ id: task._id, outcome: task.outcome }); } }}
                            onKeyDown={(e) => {
                                if (!task.isMasked && (e.key === 'Enter' || e.key === ' ')) {
                                    e.preventDefault();
                                    recentSearch.save();
                                    setOpenTask({ id: task._id, outcome: task.outcome });
                                }
                            }}
                        >
                            <span className="perf-cell perf-cell-main" data-label="Task">
                                <span className="perf-task-title">
                                    {task.isPrivate && <FontAwesomeIcon icon={faLock} title="Private task" style={{ marginRight: 5, opacity: 0.7 }} />}
                                    {task.title}
                                </span>
                                <span className="perf-task-meta">
                                    <span className={`priority-badge ${task.priority?.toLowerCase()}`}>{task.priority}</span>
                                    <span className="task-context">
                                        {task.project ? task.project.name : 'Office work'}
                                    </span>
                                    {task.recurringTaskId && (
                                        <span className="task-context" title="Generated by a recurring schedule">
                                            <FontAwesomeIcon icon={faRepeat} /> Recurring
                                        </span>
                                    )}
                                    {task.shareCount > 1 && (
                                        <span className="task-context" title={`Shared with ${task.shareCount - 1} other${task.shareCount > 2 ? 's' : ''}`}>
                                            <FontAwesomeIcon icon={faUsersRectangle} /> 1 of {task.shareCount}
                                        </span>
                                    )}
                                </span>
                            </span>
                            <span className="perf-cell" data-label="Due">
                                {shortDate(dayOf(task))}
                                {task.dueTime && <small className="perf-time">{task.dueTime}</small>}
                            </span>
                            <span className="perf-cell" data-label="Finished">
                                {task.completionAt
                                    ? <>
                                        {shortDate(task.completionAt)}
                                        <small className="perf-time">
                                            {new Date(task.completionAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                        </small>
                                    </>
                                    : <span className="perf-dash">—</span>}
                            </span>
                            <span className="perf-cell" data-label="Outcome">
                                <span className={`task-status-badge ${OUTCOME[task.outcome].cls}`}>
                                    {OUTCOME[task.outcome].label}
                                </span>
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <Pagination
                currentPage={data.pagination.currentPage}
                totalPages={data.pagination.totalPages}
                totalRecords={data.pagination.totalRecords}
                limit={data.pagination.limit}
                onPageChange={setPage}
                onLimitChange={(n) => { setLimit(n); setPage(1); }}
            />

            {openTask && (
                <TaskDetailDrawer
                    taskId={openTask.id}
                    outcome={openTask.outcome}
                    onClose={() => setOpenTask(null)}
                    onEdit={() => navigate(`/edit-task/${openTask.id}`)}
                    onOpenFull={() => navigate(`/task/${openTask.id}`)}
                />
            )}
        </div>
    );
};

export default EmployeePerformance;
